import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '@shared/hooks/usePermissions';
import { useToast } from '@shared/hooks/use-toast';
import { usePageNavigation } from '@shared/components/navigation/usePageNavigation';
import { TotalResults } from '@shared/components/ui/TotalResults/TotalResults';
import { ConfirmModal, ConfirmModalRef } from '@shared/components/ui/ConfirmModal/ConfirmModal';
import { useRates } from '../../hooks/useRates';
import { useRatesFilters } from '../../hooks/useRatesFilters';
import { useChangeRateStatus } from '../../hooks/useChangeRateStatus';
import { useGenerateRatesPdf } from '../../hooks/useGenerateRatesPdf';
import { RatesTable } from '../display/RatesTable';
import { RatesFiltersSection } from '../interaction/RatesFiltersSection';
import { RatesEmptyState } from '../feedback/RatesEmptyState';
import { Rate } from '../../models/rate.types';

export const RatesListContainer = () => {
  const { t } = useTranslation('ratesModule');
  const { toast } = useToast();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const confirmModalRef = useRef<ConfirmModalRef>(null);

  const canView = hasPermission('rates.view');
  const canManage = hasPermission('rates.manage');

  const { search, status, handleSearchChange, handleStatusChange, handleClearFilters, apiFilters, hasActiveFilters } =
    useRatesFilters();

  const { rates, isLoading, isFetching, isFetchingNextPage, fetchNextPage, hasNextPage, total } = useRates(apiFilters);

  const { mutateAsync: changeStatus } = useChangeRateStatus();
  const { generatePdf, isGenerating: isGeneratingPdf } = useGenerateRatesPdf();
  const [selectedRateIds, setSelectedRateIds] = useState<Set<number>>(new Set());

  const handleSelectionChanged = useCallback((selectedIds: Set<number>) => {
    setSelectedRateIds(selectedIds);
  }, []);

  const handleClearSelection = useCallback(() => {
    setSelectedRateIds(new Set());
  }, []);

  const handleGeneratePdf = useCallback(async () => {
    await generatePdf(Array.from(selectedRateIds));
  }, [generatePdf, selectedRateIds]);

  const handleDeactivate = (rate: Rate) => {
    confirmModalRef.current?.open(
      async () => {
        try {
          await changeStatus(rate.id);
          toast({
            title: t('toast.deactivateSuccess'),
          });
        } catch (error: any) {
          toast({
            title: t('toast.deactivateError'),
            description: error?.response?.data?.message || error.message,
            variant: 'destructive',
          });
        }
      },
      undefined,
      t('modal.deactivate.title'),
      t('modal.deactivate.description', { name: rate.name }),
      t('modal.deactivate.confirm'),
      t('modal.deactivate.cancel'),
    );
  };

  const handleCreateRate = useCallback(() => {
    navigate('/invoices/rates/new');
  }, [navigate]);

  const isEmpty = rates.length === 0 && !isFetching && !isLoading;

  // Breadcrumbs configuration
  const breadcrumbs = useMemo(
    () => [{ label: t('breadcrumb.invoices', 'Facturación') }, { label: t('list.title') }],
    [t],
  );

  // Header actions configuration
  const headerActions = useMemo(() => {
    if (!canManage) return [];

    return [
      {
        id: 'create-rate',
        label: t('actions.createRate'),
        onClick: handleCreateRate,
        variant: 'primary' as const,
        className: 'text-white',
      },
    ];
  }, [canManage, t, handleCreateRate]);

  // Register with navigation system
  usePageNavigation({ breadcrumbs, headerActions });

  if (!canView) {
    return null;
  }

  return (
    <div className="px-6 pt-4">
      <div className="mb-4">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-3 flex-wrap">
            <RatesFiltersSection
              search={search}
              status={status}
              onSearchChange={handleSearchChange}
              onStatusChange={handleStatusChange}
              onClearFilters={handleClearFilters}
              hasActiveFilters={hasActiveFilters}
            />
          </div>

          <TotalResults total={total} />
        </div>
      </div>

      {isEmpty && (
        <RatesEmptyState
          hasFilters={hasActiveFilters}
          onClearFilters={handleClearFilters}
          onCreateRate={handleCreateRate}
          canCreate={canManage}
        />
      )}

      {!isEmpty && (
        <div className="mb-4 mt-4">
          <RatesTable
            rates={rates}
            isFetching={isFetching}
            isFetchingNextPage={isFetchingNextPage}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            onDeactivate={handleDeactivate}
            canManage={canManage}
            selectedRateIds={selectedRateIds}
            onSelectionChanged={handleSelectionChanged}
            onClearSelection={handleClearSelection}
            onGeneratePdf={handleGeneratePdf}
            isGeneratingPdf={isGeneratingPdf}
          />
        </div>
      )}

      <ConfirmModal ref={confirmModalRef} />
    </div>
  );
};
